import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Separator,
} from '@e2n/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faPen } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KatalogEndpunkt } from '../gql/graphql.ts';
import {
  checkboxZustand,
  type AttributeToggle,
  type KatalogAuswahl,
} from '../hooks/useKatalogAuswahl.ts';
import { AttributAuswahlMobil } from './AttributAuswahlMobil.tsx';
import { DatenabrufListe, type DatenabrufEintrag } from './DatenabrufListe.tsx';
import { TitelFormular } from './TitelFormular.tsx';

type Schritt = 'endpunkte' | 'datenabruf';

type ZugangDrawerProps = {
  titel: string;
  endpunkte: KatalogEndpunkt[];
  katalogAuswahl: KatalogAuswahl;
  datenabruf: DatenabrufEintrag[];
  startTitel: string;
  vergebeneTitel: string[];
  absendenText: string;
  laeuft: boolean;
  onEndpunktToggle: (endpunkt: KatalogEndpunkt) => void;
  onAttributToggle: (endpunktName: string, attributName: string) => void;
  onAlleAttributeToggle: (
    endpunkt: KatalogEndpunkt,
    attribute: AttributeToggle,
  ) => void;
  onAbsenden: (titel: string) => void;
  onSchliessen: () => void;
};

export function ZugangDrawer({
  titel,
  endpunkte,
  katalogAuswahl,
  datenabruf,
  startTitel,
  vergebeneTitel,
  absendenText,
  laeuft,
  onEndpunktToggle,
  onAttributToggle,
  onAlleAttributeToggle,
  onAbsenden,
  onSchliessen,
}: ZugangDrawerProps) {
  const { t } = useTranslation();

  const [schritt, setSchritt] = useState<Schritt>('endpunkte');
  const [attributeVon, setAttributeVon] = useState<string | null>(null);

  const offenerEndpunkt = endpunkte.find(
    (endpunkt) => endpunkt.name === attributeVon,
  );

  if (offenerEndpunkt) {
    return (
      <AttributAuswahlMobil
        endpunkt={offenerEndpunkt}
        gewaehlteAttribute={katalogAuswahl[offenerEndpunkt.name] ?? []}
        onAttributToggle={(attributName) =>
          onAttributToggle(offenerEndpunkt.name, attributName)
        }
        onAlleSetzen={(attribute) =>
          onAlleAttributeToggle(offenerEndpunkt, attribute)
        }
        onFertig={() => setAttributeVon(null)}
      />
    );
  }

  return (
    <Drawer open onOpenChange={(offen) => !offen && onSchliessen()}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="text-sm">
            {schritt === 'endpunkte' ? titel : t('domain.e2nApi.karte.title')}
          </DrawerTitle>
        </DrawerHeader>

        {schritt === 'endpunkte' ? (
          <EndpunktSchritt
            endpunkte={endpunkte}
            katalogAuswahl={katalogAuswahl}
            onEndpunktToggle={onEndpunktToggle}
            onAttributeOeffnen={setAttributeVon}
            onWeiter={() => setSchritt('datenabruf')}
          />
        ) : (
          <DatenabrufSchritt
            datenabruf={datenabruf}
            startTitel={startTitel}
            vergebeneTitel={vergebeneTitel}
            absendenText={absendenText}
            laeuft={laeuft}
            onAbsenden={onAbsenden}
            onBearbeiten={() => setSchritt('endpunkte')}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}

type EndpunktSchrittProps = {
  endpunkte: KatalogEndpunkt[];
  katalogAuswahl: KatalogAuswahl;
  onEndpunktToggle: (endpunkt: KatalogEndpunkt) => void;
  onAttributeOeffnen: (endpunktName: string) => void;
  onWeiter: () => void;
};

function EndpunktSchritt({
  endpunkte,
  katalogAuswahl,
  onEndpunktToggle,
  onAttributeOeffnen,
  onWeiter,
}: EndpunktSchrittProps) {
  const { t } = useTranslation();

  const kannWeiter = Object.values(katalogAuswahl).some(
    (attribute) => attribute.length > 0,
  );

  return (
    <>
      <div className="min-h-0 overflow-y-auto px-4">
        <h2 className="text-sm font-bold text-neutrals-900">
          {t('domain.e2nApi.neuerZugang.subtitle')}
        </h2>

        <div className="mt-3 flex flex-col gap-4">
          {endpunkte.map((endpunkt) => (
            <EndpunktZeile
              key={endpunkt.name}
              endpunkt={endpunkt}
              gewaehlteAttribute={katalogAuswahl[endpunkt.name]}
              onToggle={() => onEndpunktToggle(endpunkt)}
              onAttributeOeffnen={() => onAttributeOeffnen(endpunkt.name)}
            />
          ))}
        </div>
      </div>

      <DrawerFooter className="border-t border-neutrals-200">
        <Button className="w-full" disabled={!kannWeiter} onClick={onWeiter}>
          {t('domain.e2nApi.neuerZugang.weiter')}
        </Button>
      </DrawerFooter>
    </>
  );
}

type EndpunktZeileProps = {
  endpunkt: KatalogEndpunkt;
  gewaehlteAttribute: string[] | undefined;
  onToggle: () => void;
  onAttributeOeffnen: () => void;
};

function EndpunktZeile({
  endpunkt,
  gewaehlteAttribute,
  onToggle,
  onAttributeOeffnen,
}: EndpunktZeileProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-start gap-2">
      <Checkbox
        checked={checkboxZustand(gewaehlteAttribute, endpunkt.attribute.length)}
        onCheckedChange={onToggle}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge type="pill" variant="secondary" className="px-1">
            {endpunkt.httpMethode}
          </Badge>
          <span className="truncate text-sm font-medium text-neutrals-900">
            {endpunkt.anzeigename}
          </span>
        </div>
        <p className="mt-2 text-xs text-neutrals-700">
          {endpunkt.beschreibung}
        </p>
      </div>

      <button
        type="button"
        onClick={onAttributeOeffnen}
        className="flex shrink-0 items-center gap-2 text-xs text-neutrals-700">
        {t('domain.e2nApi.neuerZugang.attributeZaehler', {
          gewaehlt: gewaehlteAttribute?.length ?? 0,
          gesamt: endpunkt.attribute.length,
        })}
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

type DatenabrufSchrittProps = {
  datenabruf: DatenabrufEintrag[];
  startTitel: string;
  vergebeneTitel: string[];
  absendenText: string;
  laeuft: boolean;
  onAbsenden: (titel: string) => void;
  onBearbeiten: () => void;
};

function DatenabrufSchritt({
  datenabruf,
  startTitel,
  vergebeneTitel,
  absendenText,
  laeuft,
  onAbsenden,
  onBearbeiten,
}: DatenabrufSchrittProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-0 overflow-y-auto px-4 pb-4">
      <DatenabrufListe datenabruf={datenabruf} />

      <Button
        variant="ghost-primary"
        className="mt-4 gap-2 px-0"
        onClick={onBearbeiten}>
        <FontAwesomeIcon icon={faPen} />
        {t('domain.e2nApi.neuerZugang.auswahlBearbeiten')}
      </Button>

      <Separator className="my-4" />

      <TitelFormular
        startTitel={startTitel}
        vergebeneTitel={vergebeneTitel}
        auswahlVorhanden={datenabruf.length > 0}
        absendenText={absendenText}
        laeuft={laeuft}
        onAbsenden={onAbsenden}
      />
    </div>
  );
}
